import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiUsers, FiClock, FiTrendingUp, FiBell, FiCalendar, FiArrowUpRight } from 'react-icons/fi';
import { dashboardAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import AdminLayout from '../../components/layout/AdminLayout';

const monthlyData = [
  { month: 'Oct', admissions: 8 },
  { month: 'Nov', admissions: 12 },
  { month: 'Dec', admissions: 5 },
  { month: 'Jan', admissions: 18 },
  { month: 'Feb', admissions: 25 },
  { month: 'Mar', admissions: 32 }
];

const statusData = [
  { name: 'Pending', value: 15, color: '#f59e0b' },
  { name: 'Approved', value: 42, color: '#10b981' },
  { name: 'Rejected', value: 8, color: '#ef4444' }
];

const recentAdmissions = [
  { id: 1, studentName: 'Asha Tamang', classApplying: 'Grade 5', parentName: 'Bimal Tamang', email: 'bimal@email.com', status: 'pending', createdAt: new Date().toISOString() },
  { id: 2, studentName: 'Rohan Shrestha', classApplying: 'Grade 9', parentName: 'Sita Shrestha', email: 'sita@email.com', status: 'approved', createdAt: new Date(Date.now() - 86400000).toISOString() },
  { id: 3, studentName: 'Priya KC', classApplying: 'Grade 11', parentName: 'Ramesh KC', email: 'ramesh@email.com', status: 'pending', createdAt: new Date(Date.now() - 172800000).toISOString() },
  { id: 4, studentName: 'Kiran Rai', classApplying: 'Grade 3', parentName: 'Sunita Rai', email: 'sunita@email.com', status: 'approved', createdAt: new Date(Date.now() - 259200000).toISOString() },
  { id: 5, studentName: 'Anjali Poudel', classApplying: 'Grade 7', parentName: 'Dipak Poudel', email: 'dipak@email.com', status: 'rejected', createdAt: new Date(Date.now() - 345600000).toISOString() }
];

const statCardColors = {
  primary: { bg: 'from-[#1D4ED8] to-[#2563EB]', icon: 'bg-white/20', text: 'text-white' },
  yellow:  { bg: 'from-amber-400 to-amber-500', icon: 'bg-white/20', text: 'text-white' },
  green:   { bg: 'from-emerald-400 to-emerald-500', icon: 'bg-white/20', text: 'text-white' },
  blue:    { bg: 'from-sky-400 to-sky-500', icon: 'bg-white/20', text: 'text-white' },
  purple:  { bg: 'from-violet-500 to-violet-600', icon: 'bg-white/20', text: 'text-white' },
};

const StatCard = ({ icon: Icon, label, value, subtext, color = 'primary', trend }) => {
  const c = statCardColors[color];
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className={`bg-gradient-to-br ${c.bg} rounded-2xl p-6 shadow-lg`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 ${c.icon} backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend && (
          <span className="flex items-center gap-1 text-[10px] font-bold bg-white/20 text-white px-2 py-1 rounded-full">
            <FiArrowUpRight className="w-3 h-3" />
            {trend}
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-white mb-1">{value}</div>
      <div className="text-white/90 font-semibold text-sm">{label}</div>
      {subtext && <p className="text-white/60 text-xs mt-1">{subtext}</p>}
    </motion.div>
  );
};

const statusBadge = (status) => {
  if (status === 'approved') return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
  if (status === 'rejected') return 'bg-red-100 text-red-600 border border-red-200';
  return 'bg-amber-100 text-amber-700 border border-amber-200';
};

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total: 65, pending: 15, thisMonth: 32, notices: 8, events: 5 });
  const [chartData, setChartData] = useState(monthlyData);
  const [admissions, setAdmissions] = useState(recentAdmissions);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await dashboardAPI.getStats();
        const d = res.data?.data;
        if (d) {
          setStats({
            total: d.summary?.admissions?.total || 0,
            pending: d.summary?.admissions?.pending || 0,
            thisMonth: d.summary?.admissions?.thisMonth || 0,
            notices: d.summary?.notices?.published || 0,
            events: d.summary?.events?.upcoming || 0,
          });
          const trend = d.charts?.monthlyAdmissionTrend;
          if (trend?.length) {
            setChartData(trend.map(t => ({ month: t.monthName, admissions: t.total })));
          }
          const recent = d.recentActivity?.admissions;
          if (recent?.length) {
            setAdmissions(recent.map(a => ({
              id: a._id,
              studentName: a.studentName,
              classApplying: a.classApplyingFor,
              email: a.email,
              status: a.status,
              createdAt: a.createdAt,
            })));
          }
        }
      } catch {
        // Use defaults
      }
    };
    fetchData();
  }, []);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <AdminLayout>
      {/* Welcome */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1e3a8a]">Dashboard Overview</h2>
        <p className="text-slate-500 text-sm mt-1">{today}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
        <StatCard icon={FiUsers} label="Total Admissions" value={stats.total} subtext="All time" color="primary" trend="+12%" />
        <StatCard icon={FiClock} label="Pending Review" value={stats.pending} subtext="Awaiting action" color="yellow" />
        <StatCard icon={FiTrendingUp} label="This Month" value={stats.thisMonth} subtext="New applications" color="green" trend="+28%" />
        <StatCard icon={FiBell} label="Active Notices" value={stats.notices} subtext="Published" color="blue" />
        <StatCard icon={FiCalendar} label="Upcoming Events" value={stats.events} subtext="Next 30 days" color="purple" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
          <h3 className="font-bold text-[#1e3a8a] text-base mb-1">Monthly Admissions</h3>
          <p className="text-slate-500 text-xs mb-6">Last 6 months overview</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e7ff" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ border: 'none', borderRadius: '12px', boxShadow: '0 4px 20px rgba(30,58,138,0.12)', fontSize: '12px' }}
                cursor={{ fill: 'rgba(37,99,235,0.06)' }}
              />
              <Bar dataKey="admissions" fill="#2563EB" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
          <h3 className="font-bold text-[#1e3a8a] text-base mb-1">Admission Status</h3>
          <p className="text-slate-500 text-xs mb-6">Current distribution</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="45%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
                stroke="none"
              >
                {statusData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Legend
                formatter={(value) => <span style={{ fontSize: '11px', color: '#475569', fontWeight: 600 }}>{value}</span>}
              />
              <Tooltip contentStyle={{ border: 'none', borderRadius: '10px', boxShadow: '0 4px 20px rgba(30,58,138,0.1)', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Admissions Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-blue-50">
          <div>
            <h3 className="font-bold text-[#1e3a8a] text-base">Recent Admissions</h3>
            <p className="text-slate-500 text-xs mt-0.5">Latest 5 applications</p>
          </div>
          <a href="/admin/admissions" className="text-[#2563EB] text-xs font-semibold hover:text-[#1D4ED8] transition-colors flex items-center gap-1">
            View All <FiArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-blue-50/50">
                <th className="px-6 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Student</th>
                <th className="px-6 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Class</th>
                <th className="px-6 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Parent</th>
                <th className="px-6 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                <th className="px-6 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              {admissions.map((adm, idx) => (
                <tr key={adm.id || idx} className="border-t border-blue-50 hover:bg-blue-50/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-[#1e3a8a] text-sm">{adm.studentName}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{adm.email}</div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell text-slate-600 text-sm">{adm.classApplying}</td>
                  <td className="px-6 py-4 hidden lg:table-cell text-slate-600 text-sm">{adm.parentName}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${statusBadge(adm.status)}`}>
                      {adm.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell text-slate-400 text-xs">
                    {formatDate(adm.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
